import classNames from 'classnames';
import { useRef } from 'react';

// Standard label + help-text wrapper used by every field on the intake form.
export const FieldShell = ({
  label,
  htmlFor,
  required,
  help,
  error,
  children,
}) => (
  <div className="flex flex-col">
    {label && (
      <label
        htmlFor={htmlFor}
        className="block text-lg font-semibold text-mmj-header mb-2"
      >
        {label}
        {required && <span className="text-red-600 ml-1">*</span>}
      </label>
    )}
    {children}
    {help && !error && (
      <p className="text-base text-gray-700 mt-2 leading-snug">{help}</p>
    )}
    {error && (
      <p
        className="text-base text-red-700 mt-2 leading-snug"
        role="alert"
        aria-live="polite"
      >
        {error}
      </p>
    )}
  </div>
);

// Large single-line text input. min-h 56px, 18px font.
export const BigTextInput = ({
  id,
  value,
  onChange,
  type = 'text',
  inputMode,
  pattern,
  maxLength,
  placeholder,
  autoComplete,
  required,
  ariaInvalid,
  suffix,
}) => {
  const inputEl = (
    <input
      id={id}
      type={type}
      inputMode={inputMode}
      pattern={pattern}
      maxLength={maxLength}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-required={required ? 'true' : undefined}
      aria-invalid={ariaInvalid ? 'true' : undefined}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full px-4 border border-gray-300 rounded-lg text-mmj-header focus:outline-none focus:ring-2 focus:ring-mmj-blue focus:border-mmj-blue"
      style={{ minHeight: '56px', fontSize: '18px' }}
    />
  );
  if (!suffix) return inputEl;
  return (
    <div className="flex items-stretch">
      <div className="flex-1">{inputEl}</div>
      <span
        className="ml-3 flex items-center justify-center px-3 bg-gray-100 border border-gray-300 rounded-lg text-mmj-header text-lg font-semibold"
        style={{ minHeight: '56px' }}
      >
        {suffix}
      </span>
    </div>
  );
};

// Large textarea (4 rows).
export const BigTextArea = ({
  id,
  value,
  onChange,
  rows = 4,
  maxLength = 2000,
  placeholder,
}) => (
  <textarea
    id={id}
    rows={rows}
    maxLength={maxLength}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="block w-full px-4 py-3 border border-gray-300 rounded-lg text-mmj-header focus:outline-none focus:ring-2 focus:ring-mmj-blue focus:border-mmj-blue"
    style={{ fontSize: '18px' }}
  />
);

// Two large pill buttons for Yes/No. Filled blue when active.
export const YesNoToggle = ({ value, onChange, id }) => (
  <div className="flex space-x-3" id={id}>
    <button
      type="button"
      onClick={() => onChange(true)}
      className={classNames(
        'flex-1 rounded-full font-bold border-2 transition-colors',
        value === true
          ? 'bg-mmj-blue text-white border-mmj-blue'
          : 'bg-white text-mmj-header border-gray-300',
      )}
      style={{ minHeight: '56px', fontSize: '18px' }}
      aria-pressed={value === true}
    >
      Yes
    </button>
    <button
      type="button"
      onClick={() => onChange(false)}
      className={classNames(
        'flex-1 rounded-full font-bold border-2 transition-colors',
        value === false
          ? 'bg-mmj-blue text-white border-mmj-blue'
          : 'bg-white text-mmj-header border-gray-300',
      )}
      style={{ minHeight: '56px', fontSize: '18px' }}
      aria-pressed={value === false}
    >
      No
    </button>
  </div>
);

// Big tappable checkbox row. The whole label is the click target.
export const BigCheckbox = ({ id, checked, onChange, children }) => (
  <label
    htmlFor={id}
    className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-300 rounded-lg hover:border-mmj-blue"
    style={{ minHeight: '56px' }}
  >
    <input
      id={id}
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 rounded border-gray-400 text-mmj-blue focus:ring-mmj-blue flex-shrink-0"
      style={{ width: '28px', height: '28px' }}
    />
    <span className="text-base md:text-lg text-mmj-header leading-snug">
      {children}
    </span>
  </label>
);

// MM / DD / YYYY date triplet with auto-advance focus. Stores the value as
// "MM/DD/YYYY" on the parent state so the same string flows verbatim through
// the provider view and the generated PDF — that's the format every state
// medical-cannabis program expects on intake forms.
//
// Legacy ISO ("YYYY-MM-DD") values written by an earlier version of this
// component are auto-migrated on read so in-progress drafts don't break.
export const DateMMDDYYYY = ({ value, onChange, error }) => {
  // Normalize the incoming value to {mm, dd, yyyy}. Accepts both the new
  // "MM/DD/YYYY" format and the legacy "YYYY-MM-DD" format, plus the "_"
  // padding both formats may use to preserve partial entries.
  const raw = (value || '').replace(/_/g, '');
  let mm = '';
  let dd = '';
  let yyyy = '';
  const isoMatch = raw.match(/^(\d{0,4})-(\d{0,2})-(\d{0,2})$/);
  if (isoMatch) {
    yyyy = isoMatch[1] || '';
    mm = isoMatch[2] || '';
    dd = isoMatch[3] || '';
  } else if (raw.includes('/')) {
    const parts = raw.split('/');
    mm = (parts[0] || '').slice(0, 2);
    dd = (parts[1] || '').slice(0, 2);
    yyyy = (parts[2] || '').slice(0, 4);
  }

  const monthRef = useRef();
  const dayRef = useRef();
  const yearRef = useRef();

  const compose = (newMm, newDd, newYyyy) => {
    if (newMm.length === 2 && newDd.length === 2 && newYyyy.length === 4) {
      onChange(`${newMm}/${newDd}/${newYyyy}`);
    } else if (!newMm && !newDd && !newYyyy) {
      onChange('');
    } else {
      // Partial value — keep something stored so the user's typing survives a
      // re-render, but mark unfilled segments with "_" so the validator can
      // tell it's incomplete.
      onChange(
        `${newMm.padEnd(2, '_')}/${newDd.padEnd(2, '_')}/${newYyyy.padEnd(
          4,
          '_',
        )}`,
      );
    }
  };

  const handleMm = (v) => {
    const cleaned = v.replace(/\D/g, '').slice(0, 2);
    compose(cleaned, dd, yyyy);
    if (cleaned.length === 2 && dayRef.current) dayRef.current.focus();
  };
  const handleDd = (v) => {
    const cleaned = v.replace(/\D/g, '').slice(0, 2);
    compose(mm, cleaned, yyyy);
    if (cleaned.length === 2 && yearRef.current) yearRef.current.focus();
  };
  const handleYyyy = (v) => {
    const cleaned = v.replace(/\D/g, '').slice(0, 4);
    compose(mm, dd, cleaned);
  };

  return (
    <FieldShell
      label="Date of birth"
      required
      htmlFor="dob_mm"
      help="Use the format MM / DD / YYYY.  Example: 01 / 15 / 1954."
      error={error}
    >
      <div className="flex items-center space-x-2">
        <input
          ref={monthRef}
          id="dob_mm"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
          placeholder="MM"
          value={mm.replace(/_/g, '')}
          onChange={(e) => handleMm(e.target.value)}
          className="w-20 text-center px-2 border border-gray-300 rounded-lg text-mmj-header focus:outline-none focus:ring-2 focus:ring-mmj-blue focus:border-mmj-blue"
          style={{ minHeight: '56px', fontSize: '22px' }}
          aria-label="Month"
        />
        <span className="text-2xl text-gray-400">/</span>
        <input
          ref={dayRef}
          id="dob_dd"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
          placeholder="DD"
          value={dd.replace(/_/g, '')}
          onChange={(e) => handleDd(e.target.value)}
          className="w-20 text-center px-2 border border-gray-300 rounded-lg text-mmj-header focus:outline-none focus:ring-2 focus:ring-mmj-blue focus:border-mmj-blue"
          style={{ minHeight: '56px', fontSize: '22px' }}
          aria-label="Day"
        />
        <span className="text-2xl text-gray-400">/</span>
        <input
          ref={yearRef}
          id="dob_yyyy"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          placeholder="YYYY"
          value={yyyy.replace(/_/g, '')}
          onChange={(e) => handleYyyy(e.target.value)}
          className="w-28 text-center px-2 border border-gray-300 rounded-lg text-mmj-header focus:outline-none focus:ring-2 focus:ring-mmj-blue focus:border-mmj-blue"
          style={{ minHeight: '56px', fontSize: '22px' }}
          aria-label="Year"
        />
      </div>
    </FieldShell>
  );
};

// "5 ft 10 in" stored as two separate keys: height_ft, height_in.
export const HeightFtIn = ({
  feet,
  inches,
  onFeetChange,
  onInchesChange,
  error,
}) => {
  const inchRef = useRef();
  const handleFeet = (v) => {
    const cleaned = v.replace(/\D/g, '').slice(0, 1);
    onFeetChange(cleaned);
    if (cleaned.length === 1 && inchRef.current) inchRef.current.focus();
  };
  const handleInches = (v) => {
    const cleaned = v.replace(/\D/g, '').slice(0, 2);
    onInchesChange(cleaned);
  };
  return (
    <FieldShell
      label="Height"
      required
      htmlFor="height_ft"
      help="Example: 5 ft 10 in."
      error={error}
    >
      <div className="flex items-center space-x-2">
        <input
          id="height_ft"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          placeholder="5"
          value={feet || ''}
          onChange={(e) => handleFeet(e.target.value)}
          className="w-20 text-center px-2 border border-gray-300 rounded-lg text-mmj-header focus:outline-none focus:ring-2 focus:ring-mmj-blue focus:border-mmj-blue"
          style={{ minHeight: '56px', fontSize: '22px' }}
          aria-label="Feet"
        />
        <span className="text-lg text-mmj-header font-semibold">ft</span>
        <input
          ref={inchRef}
          id="height_in"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={2}
          placeholder="10"
          value={inches || ''}
          onChange={(e) => handleInches(e.target.value)}
          className="w-20 text-center px-2 border border-gray-300 rounded-lg text-mmj-header focus:outline-none focus:ring-2 focus:ring-mmj-blue focus:border-mmj-blue"
          style={{ minHeight: '56px', fontSize: '22px' }}
          aria-label="Inches"
        />
        <span className="text-lg text-mmj-header font-semibold">in</span>
      </div>
    </FieldShell>
  );
};
